import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

// ── Constants ──
const VACUUM_SPEED = 5;
const VACUUM_COLLISION_R = 32;
const SUCTION_RADIUS = 110;
const NOZZLE_REACH = 31;
const COLLECT_DIST = 15;
const DUST_COUNT = 40;
const TRASH_COUNT = 10;
const TOTAL_ITEMS = DUST_COUNT + TRASH_COUNT;
const TRASH_TYPES = ['milk_carton', 'candy_wrapper', 'paper', 'soda_can', 'banana_peel'];
const OBSTACLE_COUNT = 6;

// ── Obstacle Definitions ──
const OBSTACLE_DEFS = {
  sofa:         { w: 130, h: 55,  canRotate: true  },
  armchair:     { w: 58,  h: 58,  canRotate: false },
  coffee_table: { w: 80,  h: 52,  canRotate: false },
  bookshelf:    { w: 32,  h: 95,  canRotate: true  },
  plant:        { w: 42,  h: 42,  canRotate: false },
  dining_table: { w: 105, h: 75,  canRotate: false },
  tv_stand:     { w: 115, h: 42,  canRotate: true  },
};

// ── Sound System ──
function createSoundSystem() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const osc1 = ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc1.frequency.value = 78;
    const g1 = ctx.createGain();
    g1.gain.value = 0.06;
    osc1.connect(g1);

    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = 156;
    const g2 = ctx.createGain();
    g2.gain.value = 0.025;
    osc2.connect(g2);

    const bufLen = ctx.sampleRate * 2;
    const noiseBuf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) nd[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;

    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = 700;
    bpf.Q.value = 0.6;
    const gn = ctx.createGain();
    gn.gain.value = 0.04;
    noise.connect(bpf);
    bpf.connect(gn);

    const master = ctx.createGain();
    master.gain.value = 0.55;
    g1.connect(master);
    g2.connect(master);
    gn.connect(master);
    master.connect(ctx.destination);

    osc1.start();
    osc2.start();
    noise.start();

    return {
      playCollect() {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(450, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.12);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.15, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.15);
      },
      stop() {
        master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.25);
        setTimeout(() => {
          try { osc1.stop(); } catch (e) {}
          try { osc2.stop(); } catch (e) {}
          try { noise.stop(); } catch (e) {}
          ctx.close();
        }, 350);
      },
    };
  } catch (e) {
    return null;
  }
}

// ── Obstacle Generation ──
function generateBooksData(w, h) {
  const colors = ['#e53935','#1e88e5','#43a047','#fb8c00','#8e24aa','#00bcd4','#f06292'];
  const books = [];
  const shelfCount = Math.max(2, Math.floor(h / 25));
  const shelfH = h / shelfCount;
  for (let s = 0; s < shelfCount; s++) {
    let bx = -w / 2 + 3;
    const sy = -h / 2 + s * shelfH + 4;
    const bh = shelfH - 8;
    while (bx < w / 2 - 4) {
      const bw = 4 + Math.floor(Math.random() * 7);
      const fw = Math.min(bw, w / 2 - 4 - bx);
      if (fw > 1) books.push({ x: bx, y: sy, w: fw, h: bh, color: colors[Math.floor(Math.random() * colors.length)] });
      bx += bw + 1;
    }
  }
  return books;
}

function generateObstacles(canvasW, canvasH, vacX, vacY) {
  const types = Object.keys(OBSTACLE_DEFS);
  const selected = [...types].sort(() => Math.random() - 0.5).slice(0, OBSTACLE_COUNT);
  const WALL = 65, TOP = 80, VAC_CLEAR = 155, OBS_GAP = 28;
  const placed = [];

  for (const type of selected) {
    const def = OBSTACLE_DEFS[type];
    let w = def.w, h = def.h;
    const rotated = def.canRotate && Math.random() > 0.5;
    if (rotated) [w, h] = [h, w];

    for (let attempt = 0; attempt < 80; attempt++) {
      const x = WALL + Math.random() * (canvasW - 2 * WALL - w);
      const y = TOP + Math.random() * (canvasH - TOP - WALL - h);
      const cx = x + w / 2, cy = y + h / 2;
      if (Math.sqrt((cx - vacX) ** 2 + (cy - vacY) ** 2) < VAC_CLEAR + Math.max(w, h) / 2) continue;

      let collides = false;
      for (const p of placed) {
        if (x < p.x + p.w + OBS_GAP && x + w + OBS_GAP > p.x && y < p.y + p.h + OBS_GAP && y + h + OBS_GAP > p.y) {
          collides = true; break;
        }
      }
      if (collides) continue;

      const obs = { type, x, y, w, h };
      if (type === 'bookshelf') obs.books = generateBooksData(w, h);
      placed.push(obs);
      break;
    }
  }
  return placed;
}

function isBlocked(px, py, obstacles, pad = 18) {
  return obstacles.some(o => px > o.x - pad && px < o.x + o.w + pad && py > o.y - pad && py < o.y + o.h + pad);
}

function resolveCollisions(cx, cy, obstacles) {
  let x = cx, y = cy;
  const R = VACUUM_COLLISION_R;
  for (let pass = 0; pass < 2; pass++) {
    for (const o of obstacles) {
      const nearX = Math.max(o.x, Math.min(x, o.x + o.w));
      const nearY = Math.max(o.y, Math.min(y, o.y + o.h));
      const dx = x - nearX, dy = y - nearY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < R) {
        if (dist === 0) {
          const oL = x - o.x + R, oR = o.x + o.w - x + R, oT = y - o.y + R, oB = o.y + o.h - y + R;
          const min = Math.min(oL, oR, oT, oB);
          if (min === oL) x = o.x - R;
          else if (min === oR) x = o.x + o.w + R;
          else if (min === oT) y = o.y - R;
          else y = o.y + o.h + R;
        } else {
          const overlap = R - dist;
          x += (dx / dist) * overlap;
          y += (dy / dist) * overlap;
        }
      }
    }
  }
  return { x, y };
}

// ── Obstacle Drawing ──
function drawSofa(ctx, w, h) {
  // Back
  ctx.fillStyle = '#5d4037';
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h * 0.28, 4); ctx.fill();
  // Seat
  ctx.fillStyle = '#795548';
  ctx.beginPath(); ctx.roundRect(-w / 2 + 2, -h / 2 + h * 0.28, w - 4, h * 0.55, 3); ctx.fill();
  // Cushion divider
  ctx.strokeStyle = '#6d4c41'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, -h / 2 + h * 0.28); ctx.lineTo(0, h / 2 - h * 0.17); ctx.stroke();
  // Armrests
  ctx.fillStyle = '#4e342e';
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2 + h * 0.18, 15, h * 0.72, 4); ctx.fill();
  ctx.beginPath(); ctx.roundRect(w / 2 - 15, -h / 2 + h * 0.18, 15, h * 0.72, 4); ctx.fill();
  // Base
  ctx.fillStyle = '#3e2723';
  ctx.fillRect(-w / 2 + 2, h / 2 - h * 0.17, w - 4, h * 0.17);
}

function drawArmchair(ctx, w, h) {
  ctx.fillStyle = '#4a148c';
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h * 0.26, 4); ctx.fill();
  ctx.fillStyle = '#6a1b9a';
  ctx.beginPath(); ctx.roundRect(-w / 2 + 2, -h / 2 + h * 0.26, w - 4, h * 0.57, 3); ctx.fill();
  ctx.fillStyle = '#38006b';
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2 + h * 0.16, 13, h * 0.72, 4); ctx.fill();
  ctx.beginPath(); ctx.roundRect(w / 2 - 13, -h / 2 + h * 0.16, 13, h * 0.72, 4); ctx.fill();
  ctx.fillStyle = '#2d004e';
  ctx.fillRect(-w / 2 + 2, h / 2 - h * 0.17, w - 4, h * 0.17);
}

function drawCoffeeTable(ctx, w, h) {
  const grad = ctx.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
  grad.addColorStop(0, '#d7ccc8'); grad.addColorStop(1, '#a1887f');
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 6); ctx.fill();
  ctx.strokeStyle = '#795548'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 6); ctx.stroke();
  ctx.fillStyle = '#4e342e';
  for (const [lx, ly] of [[-w/2+8,-h/2+8],[w/2-8,-h/2+8],[-w/2+8,h/2-8],[w/2-8,h/2-8]]) {
    ctx.beginPath(); ctx.arc(lx, ly, 5, 0, Math.PI * 2); ctx.fill();
  }
}

function drawBookshelf(ctx, w, h, books) {
  ctx.fillStyle = '#4e342e';
  ctx.fillRect(-w / 2, -h / 2, w, h);
  if (books) {
    for (const b of books) { ctx.fillStyle = b.color; ctx.fillRect(b.x, b.y, b.w, b.h); }
  }
  const shelfCount = Math.max(2, Math.floor(h / 25));
  ctx.fillStyle = '#3e2723';
  for (let s = 1; s < shelfCount; s++) {
    ctx.fillRect(-w / 2, -h / 2 + (h / shelfCount) * s - 1.5, w, 3);
  }
  ctx.strokeStyle = '#3e2723'; ctx.lineWidth = 1.5;
  ctx.strokeRect(-w / 2, -h / 2, w, h);
}

function drawPlant(ctx, w, h) {
  const r = Math.min(w, h) / 2;
  // Pot
  ctx.fillStyle = '#bf6e2e';
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, r * 0.2); ctx.lineTo(-r * 0.4, r);
  ctx.lineTo(r * 0.4, r); ctx.lineTo(r * 0.6, r * 0.2); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#d4813a';
  ctx.beginPath(); ctx.ellipse(0, r * 0.22, r * 0.65, r * 0.15, 0, 0, Math.PI * 2); ctx.fill();
  // Leaves
  ctx.fillStyle = '#1b5e20';
  ctx.beginPath(); ctx.arc(-r * 0.3, -r * 0.3, r * 0.65, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#2e7d32';
  ctx.beginPath(); ctx.arc(r * 0.3, -r * 0.25, r * 0.55, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#388e3c';
  ctx.beginPath(); ctx.arc(0, -r * 0.5, r * 0.48, 0, Math.PI * 2); ctx.fill();
}

function drawDiningTable(ctx, w, h) {
  const grad = ctx.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
  grad.addColorStop(0, '#c8b59e'); grad.addColorStop(1, '#8d6e63');
  ctx.fillStyle = grad;
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 6); ctx.fill();
  ctx.strokeStyle = '#6d4c41'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 6); ctx.stroke();
  const plateXs = w > 90 ? [-w / 3, 0, w / 3] : [-w / 3, w / 3];
  for (const px of plateXs) {
    ctx.fillStyle = 'white'; ctx.beginPath(); ctx.arc(px, 0, 9, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#bdbdbd'; ctx.lineWidth = 1; ctx.stroke();
  }
}

function drawTVStand(ctx, w, h) {
  ctx.fillStyle = '#37474f';
  ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 4); ctx.fill();
  ctx.fillStyle = '#1a1a2e';
  ctx.beginPath(); ctx.roundRect(-w / 2 + 6, -h / 2 + 5, w - 12, h * 0.6, 3); ctx.fill();
  ctx.fillStyle = 'rgba(120,200,255,0.08)';
  ctx.beginPath(); ctx.roundRect(-w / 2 + 6, -h / 2 + 5, w - 12, h * 0.6, 3); ctx.fill();
  ctx.fillStyle = '#00e5ff';
  ctx.beginPath(); ctx.arc(w / 2 - 12, 4, 2.5, 0, Math.PI * 2); ctx.fill();
}

const OBSTACLE_DRAWERS = {
  sofa: drawSofa,
  armchair: drawArmchair,
  coffee_table: drawCoffeeTable,
  bookshelf: drawBookshelf,
  plant: drawPlant,
  dining_table: drawDiningTable,
  tv_stand: drawTVStand,
};

function drawObstacle(ctx, obs) {
  ctx.save();
  ctx.translate(obs.x + obs.w / 2, obs.y + obs.h / 2);
  if (obs.type === 'bookshelf') {
    drawBookshelf(ctx, obs.w, obs.h, obs.books);
  } else {
    OBSTACLE_DRAWERS[obs.type](ctx, obs.w, obs.h);
  }
  ctx.restore();
}

// ── Trash Drawing ──
function drawMilkCarton(ctx, x, y) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#e8f5e9'; ctx.fillRect(-9, -12, 18, 24);
  ctx.fillStyle = '#42a5f5'; ctx.fillRect(-9, -12, 18, 9);
  ctx.fillStyle = 'white'; ctx.fillRect(-6, 0, 12, 7);
  ctx.strokeStyle = '#90a4ae'; ctx.lineWidth = 1; ctx.strokeRect(-9, -12, 18, 24);
  ctx.beginPath(); ctx.moveTo(-9, -3); ctx.lineTo(0, -7); ctx.lineTo(9, -3);
  ctx.strokeStyle = '#78909c'; ctx.lineWidth = 0.8; ctx.stroke();
  ctx.restore();
}

function drawCandyWrapper(ctx, x, y) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#ff7043';
  ctx.beginPath(); ctx.moveTo(-13,-2); ctx.lineTo(-7,-5); ctx.lineTo(-7,5); ctx.lineTo(-13,2); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(13,-2); ctx.lineTo(7,-5); ctx.lineTo(7,5); ctx.lineTo(13,2); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#ffab40'; ctx.fillRect(-7, -5, 14, 10);
  ctx.fillStyle = '#ff7043'; ctx.fillRect(-7, -1, 14, 2);
  ctx.restore();
}

function drawCrumpledPaper(ctx, x, y) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#fafafa';
  ctx.beginPath();
  ctx.moveTo(-6,-9); ctx.lineTo(5,-7); ctx.lineTo(9,-1); ctx.lineTo(6,7); ctx.lineTo(-2,9); ctx.lineTo(-9,3); ctx.lineTo(-7,-4);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = '#e0e0e0'; ctx.lineWidth = 0.7; ctx.stroke();
  ctx.strokeStyle = '#bdbdbd'; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(-4,-5); ctx.lineTo(2,4); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(2,-5); ctx.lineTo(-3,6); ctx.stroke();
  ctx.restore();
}

function drawSodaCan(ctx, x, y) {
  ctx.save(); ctx.translate(x, y);
  const grad = ctx.createRadialGradient(-2,-2,1,0,0,10);
  grad.addColorStop(0,'#ef5350'); grad.addColorStop(1,'#b71c1c');
  ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(0,0,10,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle = '#bdbdbd'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(0,0,7.5,0,Math.PI*2); ctx.stroke();
  ctx.fillStyle = '#bdbdbd'; ctx.beginPath(); ctx.ellipse(0,-2,3,1.5,0,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.beginPath(); ctx.ellipse(-3,-3,3,2,-0.5,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

function drawBananaPeel(ctx, x, y) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#fdd835';
  ctx.beginPath(); ctx.arc(0,0,4,0,Math.PI*2); ctx.fill();
  for (let i = 0; i < 4; i++) {
    ctx.save(); ctx.rotate((i * Math.PI) / 2 + 0.3);
    ctx.beginPath(); ctx.moveTo(0,-3); ctx.quadraticCurveTo(5,-8,2,-14); ctx.quadraticCurveTo(-1,-9,0,-3); ctx.fill();
    ctx.restore();
  }
  ctx.fillStyle = '#8d6e63';
  ctx.beginPath(); ctx.arc(2,-9,1,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(-6,3,0.8,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

const TRASH_DRAWERS = {
  milk_carton: drawMilkCarton, candy_wrapper: drawCandyWrapper,
  paper: drawCrumpledPaper, soda_can: drawSodaCan, banana_peel: drawBananaPeel,
};

function drawDustParticle(ctx, x, y) {
  ctx.save();
  const grad = ctx.createRadialGradient(x,y,0,x,y,9);
  grad.addColorStop(0,'rgba(170,160,148,0.92)');
  grad.addColorStop(0.55,'rgba(155,145,132,0.5)');
  grad.addColorStop(1,'rgba(140,130,118,0)');
  ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(x,y,9,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = '#a09080'; ctx.beginPath(); ctx.arc(x,y,3.5,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

function drawItem(ctx, item) {
  if (!item.alive) return;
  if (item.type === 'dust') drawDustParticle(ctx, item.x, item.y);
  else TRASH_DRAWERS[item.type](ctx, item.x, item.y);
}

// ── Vacuum Drawing ──
function drawVacuum(ctx, vacuum) {
  ctx.save();
  ctx.translate(vacuum.x, vacuum.y);
  ctx.rotate(vacuum.angle);

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.2)'; ctx.shadowBlur = 6; ctx.shadowOffsetY = 3;
  ctx.fillStyle = '#616161'; ctx.beginPath(); ctx.roundRect(-52,-5,12,10,4); ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#9e9e9e'; ctx.beginPath(); ctx.roundRect(-42,-3.5,24,7,3); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.15)'; ctx.fillRect(-40,-3.5,20,3);

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.28)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 6;
  const bg = ctx.createLinearGradient(0,-18,0,18);
  bg.addColorStop(0,'#546e7a'); bg.addColorStop(0.5,'#37474f'); bg.addColorStop(1,'#263238');
  ctx.fillStyle = bg; ctx.beginPath(); ctx.roundRect(-20,-18,40,36,8); ctx.fill();
  ctx.restore();

  ctx.fillStyle = 'rgba(255,255,255,0.08)'; ctx.beginPath(); ctx.roundRect(-17,-15,34,14,5); ctx.fill();
  ctx.fillStyle = '#e53935'; ctx.fillRect(-16,-2,32,4);
  ctx.fillStyle = '#76ff03'; ctx.beginPath(); ctx.arc(-10,8,2.5,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = 'rgba(118,255,3,0.3)'; ctx.beginPath(); ctx.arc(-10,8,5,0,Math.PI*2); ctx.fill();

  ctx.fillStyle = '#212121';
  ctx.beginPath(); ctx.ellipse(-6,-20,7,3.5,0,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(-6,20,7,3.5,0,0,Math.PI*2); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.beginPath(); ctx.ellipse(-6,-21,5,2,0,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(-6,19,5,2,0,0,Math.PI*2); ctx.fill();

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.22)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 4;
  const hg = ctx.createLinearGradient(0,-24,0,24);
  hg.addColorStop(0,'#424242'); hg.addColorStop(1,'#1a1a1a');
  ctx.fillStyle = hg; ctx.beginPath(); ctx.roundRect(18,-24,14,48,3); ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#ef5350'; ctx.fillRect(28,-22,3,44);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 0.8;
  for (let by = -20; by <= 20; by += 4) { ctx.beginPath(); ctx.moveTo(19,by); ctx.lineTo(30,by); ctx.stroke(); }

  ctx.restore();
}

// ── Background ──
function drawBackground(ctx, w, h) {
  ctx.fillStyle = '#f5edd6'; ctx.fillRect(0,0,w,h);
  ctx.strokeStyle = 'rgba(190,155,90,0.2)'; ctx.lineWidth = 1;
  for (let y = 0; y < h; y += 48) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(w,y); ctx.stroke(); }
  ctx.strokeStyle = 'rgba(190,155,90,0.07)';
  for (let x = 0; x < w; x += 130) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x+25,h); ctx.stroke(); }
}

// ── Suction Waves ──
function drawSuctionWaves(ctx, vacuum, time) {
  const period = 700;
  for (let i = 0; i < 3; i++) {
    const phase = ((time % period) / period + i / 3) % 1;
    ctx.save();
    ctx.strokeStyle = `rgba(200,80,80,${(1-phase)*0.15})`; ctx.lineWidth = 1.5;
    ctx.setLineDash([4,4]);
    ctx.beginPath(); ctx.arc(vacuum.x, vacuum.y, phase * SUCTION_RADIUS, 0, Math.PI*2); ctx.stroke();
    ctx.restore();
  }
}

// ── Joystick Drawing ──
// joystick: { baseX, baseY, radius, knobRadius, knobX, knobY, active }
// homeX/homeY: the fixed corner position shown when inactive
function drawJoystick(ctx, joystick, homeX, homeY) {
  ctx.save();
  if (!joystick.active) {
    // Faded hint in the corner
    ctx.globalAlpha = 0.32;
    ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(homeX, homeY, joystick.radius, 0, Math.PI*2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.beginPath(); ctx.arc(homeX, homeY, joystick.knobRadius, 0, Math.PI*2); ctx.fill();
  } else {
    ctx.globalAlpha = 0.86;
    // Outer ring
    ctx.fillStyle = 'rgba(255,255,255,0.13)'; ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(joystick.baseX, joystick.baseY, joystick.radius, 0, Math.PI*2); ctx.fill(); ctx.stroke();
    // Directional notches
    ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.lineWidth = 1.5;
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2;
      ctx.beginPath();
      ctx.moveTo(joystick.baseX + Math.cos(a)*(joystick.radius-14), joystick.baseY + Math.sin(a)*(joystick.radius-14));
      ctx.lineTo(joystick.baseX + Math.cos(a)*(joystick.radius-6), joystick.baseY + Math.sin(a)*(joystick.radius-6));
      ctx.stroke();
    }
    // Knob
    ctx.fillStyle = 'rgba(255,255,255,0.72)';
    ctx.beginPath(); ctx.arc(joystick.knobX, joystick.knobY, joystick.knobRadius, 0, Math.PI*2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)'; ctx.lineWidth = 2; ctx.stroke();
  }
  ctx.restore();
}

// ── HUD ──
function drawHUD(ctx, score, w, h) {
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.beginPath(); ctx.roundRect(w/2-130,14,260,44,22); ctx.fill();
  ctx.fillStyle = 'white'; ctx.font = 'bold 17px Poppins, sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(`${score} / ${TOTAL_ITEMS} items cleaned!`, w/2, 36);

  ctx.fillStyle = 'rgba(0,0,0,0.32)';
  ctx.beginPath(); ctx.roundRect(w/2-160,h-50,320,34,17); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.font = '13px sans-serif';
  ctx.fillText('Arrow keys, WASD, or touch joystick (bottom-left)', w/2, h-33);
  ctx.restore();
}

// ── Canvas Game Component ──
function VacuuGameCanvas({ onWin }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const vacuum = { x: canvas.width / 2, y: canvas.height / 2, angle: 0 };

    const obstacles = generateObstacles(canvas.width, canvas.height, vacuum.x, vacuum.y);

    // Generate items, avoiding obstacles
    const items = [];
    for (let i = 0; i < DUST_COUNT; i++) {
      let x, y, att = 0;
      do {
        x = 30 + Math.random() * (canvas.width - 60);
        y = 80 + Math.random() * (canvas.height - 130);
        att++;
      } while (att < 100 && (Math.sqrt((x - vacuum.x) ** 2 + (y - vacuum.y) ** 2) < 160 || isBlocked(x, y, obstacles, 12)));
      items.push({ id: i, x, y, alive: true, type: 'dust' });
    }
    for (let i = 0; i < TRASH_COUNT; i++) {
      let x, y, att = 0;
      do {
        x = 40 + Math.random() * (canvas.width - 80);
        y = 80 + Math.random() * (canvas.height - 140);
        att++;
      } while (att < 100 && (Math.sqrt((x - vacuum.x) ** 2 + (y - vacuum.y) ** 2) < 160 || isBlocked(x, y, obstacles, 15)));
      items.push({ id: DUST_COUNT + i, x, y, alive: true, type: TRASH_TYPES[Math.floor(Math.random() * TRASH_TYPES.length)] });
    }

    // Joystick — floats to wherever you touch
    const JR = Math.min(55, Math.max(38, canvas.width * 0.09));
    const homeX = JR + 30;
    const homeY = () => canvas.height - JR - 20;
    const joystick = {
      baseX: homeX, baseY: homeY(),
      radius: JR, knobRadius: JR * 0.52,
      knobX: homeX, knobY: homeY(),
      active: false, touchId: null,
    };

    const keys = {};
    let score = 0, animFrame, won = false, sound = null;

    function initSound() {
      if (sound) return;
      sound = createSoundSystem();
    }

    // Keyboard
    const handleKeyDown = (e) => {
      keys[e.key] = true;
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault();
      initSound();
    };
    const handleKeyUp = (e) => { keys[e.key] = false; };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Touch — floating joystick: touch anywhere to place it
    const handleTouchStart = (e) => {
      e.preventDefault();
      initSound();
      if (joystick.touchId !== null) return;
      const touch = e.changedTouches[0];
      joystick.touchId = touch.identifier;
      joystick.active = true;
      joystick.baseX = touch.clientX;
      joystick.baseY = touch.clientY;
      joystick.knobX = touch.clientX;
      joystick.knobY = touch.clientY;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      for (const touch of e.changedTouches) {
        if (touch.identifier !== joystick.touchId) continue;
        const dx = touch.clientX - joystick.baseX;
        const dy = touch.clientY - joystick.baseY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > joystick.radius) {
          joystick.knobX = joystick.baseX + (dx / dist) * joystick.radius;
          joystick.knobY = joystick.baseY + (dy / dist) * joystick.radius;
        } else {
          joystick.knobX = touch.clientX;
          joystick.knobY = touch.clientY;
        }
      }
    };

    const handleTouchEnd = (e) => {
      for (const touch of e.changedTouches) {
        if (touch.identifier === joystick.touchId) {
          joystick.touchId = null;
          joystick.active = false;
          joystick.baseX = homeX;
          joystick.baseY = homeY();
          joystick.knobX = homeX;
          joystick.knobY = homeY();
        }
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (!joystick.active) {
        joystick.baseX = homeX;
        joystick.baseY = homeY();
        joystick.knobX = homeX;
        joystick.knobY = homeY();
      }
    };
    window.addEventListener('resize', handleResize);

    function gameLoop(time) {
      // Keyboard input
      let dx = 0, dy = 0;
      if (keys['ArrowUp']  || keys['w'] || keys['W']) dy -= 1;
      if (keys['ArrowDown'] || keys['s'] || keys['S']) dy += 1;
      if (keys['ArrowLeft'] || keys['a'] || keys['A']) dx -= 1;
      if (keys['ArrowRight']|| keys['d'] || keys['D']) dx += 1;

      // Joystick input
      if (joystick.active) {
        const jdx = joystick.knobX - joystick.baseX;
        const jdy = joystick.knobY - joystick.baseY;
        const jLen = Math.sqrt(jdx * jdx + jdy * jdy);
        if (jLen > 4) { dx += jdx / joystick.radius; dy += jdy / joystick.radius; }
      }

      if (dx !== 0 || dy !== 0) {
        const len = Math.sqrt(dx * dx + dy * dy);
        const raw = resolveCollisions(
          Math.max(35, Math.min(canvas.width  - 35, vacuum.x + (dx / len) * VACUUM_SPEED)),
          Math.max(35, Math.min(canvas.height - 35, vacuum.y + (dy / len) * VACUUM_SPEED)),
          obstacles
        );
        vacuum.x = raw.x;
        vacuum.y = raw.y;
        vacuum.angle = Math.atan2(dy, dx);
      }

      // Nozzle tip
      const tipX = vacuum.x + Math.cos(vacuum.angle) * NOZZLE_REACH;
      const tipY = vacuum.y + Math.sin(vacuum.angle) * NOZZLE_REACH;

      // Update items
      let collected = 0;
      items.forEach((item) => {
        if (!item.alive) return;
        const cx = item.x - vacuum.x, cy = item.y - vacuum.y;
        const distCenter = Math.sqrt(cx * cx + cy * cy);
        const nx = item.x - tipX, ny = item.y - tipY;
        const distNozzle = Math.sqrt(nx * nx + ny * ny);
        if (distNozzle < COLLECT_DIST) {
          item.alive = false; collected++;
          if (sound) sound.playCollect();
        } else if (distCenter < SUCTION_RADIUS) {
          const px = tipX - item.x, py = tipY - item.y;
          const pd = Math.sqrt(px * px + py * py);
          const str = ((SUCTION_RADIUS - distCenter) / SUCTION_RADIUS) * 4;
          item.x += (px / pd) * str;
          item.y += (py / pd) * str;
        }
      });

      if (collected > 0) {
        score += collected;
        if (score >= TOTAL_ITEMS && !won) {
          won = true;
          if (sound) { sound.stop(); sound = null; }
          onWin();
        }
      }

      // Draw
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawBackground(ctx, canvas.width, canvas.height);
      obstacles.forEach((obs) => drawObstacle(ctx, obs));
      drawSuctionWaves(ctx, vacuum, time);
      items.forEach((item) => drawItem(ctx, item));
      drawVacuum(ctx, vacuum);
      drawJoystick(ctx, joystick, homeX, homeY());
      drawHUD(ctx, score, canvas.width, canvas.height);

      if (!won) animFrame = requestAnimationFrame(gameLoop);
    }

    animFrame = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animFrame);
      if (sound) { sound.stop(); sound = null; }
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onWin]);

  return <canvas ref={canvasRef} style={{ display: 'block' }} />;
}

// ── Main Component ──
export default function VacuuGame() {
  const [gameKey, setGameKey] = useState(0);
  const [won, setWon] = useState(false);
  const handleWin = useCallback(() => { setWon(true); }, []);
  const handleRestart = () => { setWon(false); setGameKey((k) => k + 1); };

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <Helmet>
        <title>Vacuum - Lowell Handy</title>
        <meta property="og:title" content="Vacuum" />
        <meta property="og:description" content="Builds by Lowell Handy" />
      </Helmet>
      <VacuuGameCanvas key={gameKey} onWin={handleWin} />
      {won && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10 }}>
          <div style={{ background:'white',borderRadius:24,padding:'48px 64px',textAlign:'center',fontFamily:'Poppins, sans-serif',boxShadow:'0 24px 64px rgba(0,0,0,0.35)' }}>
            <div style={{ fontSize:88 }}>&#10024;</div>
            <h1 style={{ fontSize:42,margin:'12px 0 8px',color:'#2e7d32' }}>All Clean!</h1>
            <p style={{ fontSize:19,color:'#666',margin:'0 0 28px' }}>You cleaned up all {TOTAL_ITEMS} items!</p>
            <button onClick={handleRestart} style={{ padding:'14px 40px',fontSize:20,background:'#43a047',color:'white',border:'none',borderRadius:40,cursor:'pointer',fontFamily:'Poppins, sans-serif',fontWeight:700,boxShadow:'0 4px 18px rgba(67,160,71,0.45)' }}>
              Play Again!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
